import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import AccountingAnomaliesCard from '@/components/AccountingAnomaliesCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FinancialHealthTab — Round 93n (Jun 2026), restyled Round 93n+ to
 * match the Stock Metrics & Insights tabs (fuchsia section titles,
 * MetricRow flex layout, bigger / bolder values). Added 5-year
 * sparklines on the Income panel.
 *
 * Source: Massive Financials & Ratios. No Yahoo / yfinance
 * anywhere on this path.
 *
 * Open to every visitor (anonymous + free + logged-in).
 */
export default function FinancialHealthTab({ stock }) {
  const ticker = stock?.ticker;
  const [state, setState] = useState({ data: null, loading: false, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!ticker) return undefined;
    // Canonical async data-fetch pattern (same as StockMetricsTab,
    // MassiveInsightsTab, etc.) — `cancelled` flag short-circuits
    // setState if the effect re-runs before the request resolves.
    setState({ data: null, loading: true, error: null });
    axios
      .get(`${API}/stocks/${ticker}/financial-health`)
      .then((r) => {
        if (!cancelled) setState({ data: r.data, loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            data: null,
            loading: false,
            error: e?.response?.data?.detail || e?.message || 'Fetch failed',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  const { data, loading, error } = state;

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground" data-testid="financial-health-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading Massive financials…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-4 text-sm text-rose-300" data-testid="financial-health-error">
        Couldn&apos;t load financial data: {error}
      </div>
    );
  }
  if (!data || !data.has_data) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="financial-health-empty">
        <p className="font-semibold mb-1">No Massive financials available for {ticker}.</p>
        <p className="text-xs leading-relaxed font-semibold">
          ETFs, mutual funds, and many foreign-filer ADRs don&apos;t file their own balance sheet or income statement —
          they pass through the financials of their underlying holdings. Open the <strong>Stock Metrics</strong> tab
          for the fund profile, or the <strong>Insights</strong> tab for the underlying-holdings view.
        </p>
      </div>
    );
  }

  const ratios = data.ratios || {};
  const income = data.income || {};
  const cashFlow = data.cash_flow || {};
  const bs = data.balance_sheet || {};

  return (
    <div className="space-y-1" data-testid="financial-health-tab">
      <div className="mb-3"><AccountingAnomaliesCard ticker={stock?.ticker} /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
        {/* Left column: Live Ratios + Cash Flow */}
        <div>
          <SectionHeader title="Live Ratios (Daily — Massive)" />
          <MetricRow label="Market Cap" defKey="MARKET_CAP" value={fmtMoney(ratios.market_cap)} />
          <MetricRow label="Enterprise Value" defKey="ENTERPRISE_VALUE" value={fmtMoney(ratios.enterprise_value)} />
          <MetricRow label="P/E" defKey="TRAILING_PE" value={fmtRatio(ratios.price_to_earnings)} />
          <MetricRow label="P/B" defKey="PRICE_BOOK" value={fmtRatio(ratios.price_to_book)} />
          <MetricRow label="P/S" defKey="PRICE_SALES" value={fmtRatio(ratios.price_to_sales)} />
          <MetricRow label="P/CF" defKey="PRICE_CASHFLOW" value={fmtRatio(ratios.price_to_cash_flow)} />
          <MetricRow label="P/FCF" defKey="PRICE_CASHFLOW" value={fmtRatio(ratios.price_to_free_cash_flow)} />
          <MetricRow label="EV/EBITDA" defKey="EV_EBITDA" value={fmtMultiple(ratios.ev_to_ebitda)} />
          <MetricRow label="D/E" defKey="DEBT_EQUITY" value={fmtRatio(ratios.debt_to_equity)} />
          <MetricRow label="ROE" defKey="ROE" value={fmtPct(ratios.return_on_equity)} />
          <MetricRow label="ROA" defKey="ROA" value={fmtPct(ratios.return_on_assets)} />
          <MetricRow label="Current Ratio" defKey="CURRENT_RATIO" value={fmtRatio(ratios.current)} />
          <MetricRow label="Quick Ratio" defKey="QUICK_RATIO" value={fmtRatio(ratios.quick)} />
          <MetricRow label="Cash Ratio" defKey="CASH_RATIO" value={fmtRatio(ratios.cash)} />

          <SectionHeader title="Cash Flow (TTM — Massive)" />
          <MetricRow label="Cash from Operations" defKey="CFO" value={fmtMoney(cashFlow.cfo)} />
          <MetricRow label="Capex" defKey="CAPEX" value={fmtMoney(cashFlow.capex)} />
          <MetricRow
            label="Free Cash Flow"
            defKey="FREE_CASH_FLOW"
            value={fmtMoney(cashFlow.free_cash_flow)}
            tone={cashFlow.free_cash_flow > 0 ? 'good' : cashFlow.free_cash_flow < 0 ? 'bad' : undefined}
          />
          <MetricRow label="Dividends Paid" defKey="DRIP" value={fmtMoney(cashFlow.dividends_paid)} />
          <MetricRow
            label="Dividend FCF Coverage"
            defKey="DIVIDEND_FCF_COVERAGE"
            value={fmtMultiple(cashFlow.dividend_fcf_coverage)}
            tone={coverageTone(cashFlow.dividend_fcf_coverage)}
            hint={coverageHint(cashFlow.dividend_fcf_coverage)}
          />
          <MetricRow label="Depreciation &amp; Amort" defKey="FCF" value={fmtMoney(cashFlow.depreciation)} />
        </div>

        {/* Right column: Income + Balance Sheet */}
        <div>
          <SectionHeader title="Income Statement (TTM — Massive)" />
          <MetricRow label="Revenue (TTM)" defKey="TOTAL_REVENUE" value={fmtMoney(income.revenue)} />
          <MetricRow
            label="Revenue YoY"
            defKey="REVENUE_GROWTH"
            value={fmtPct(income.revenue_yoy, 1)}
            tone={income.revenue_yoy > 0.05 ? 'good' : income.revenue_yoy < 0 ? 'bad' : undefined}
          />
          <MetricRow label="Gross Margin" defKey="GROSS_MARGIN" value={fmtPct(income.gross_margin, 1)} />
          <MetricRow label="Operating Margin" defKey="OPERATING_MARGIN" value={fmtPct(income.operating_margin, 1)} />
          <MetricRow label="Net Margin" defKey="NET_MARGIN" value={fmtPct(income.net_margin, 1)} />
          <MetricRow label="EBITDA" defKey="EBITDA" value={fmtMoney(income.ebitda)} />
          <MetricRow label="Net Income" defKey="EPS" value={fmtMoney(income.net_income)} />
          <MetricRow
            label="Diluted EPS"
            defKey="EPS"
            value={income.diluted_eps !== null && income.diluted_eps !== undefined ? `$${Number(income.diluted_eps).toFixed(2)}` : '—'}
          />
          <MetricRow
            label="Diluted Shares"
            defKey="SHARES_OUTSTANDING"
            value={fmtMoney(income.diluted_shares_outstanding)}
            tone={income.share_count_yoy > 0.01 ? 'bad' : income.share_count_yoy < -0.005 ? 'good' : undefined}
            hint={
              income.share_count_yoy !== null && income.share_count_yoy !== undefined
                ? `${income.share_count_yoy > 0 ? '+' : ''}${(income.share_count_yoy * 100).toFixed(2)}% YoY`
                : undefined
            }
          />

          <SparklineRow history={income.history || []} paysDividend={stock?.pays_dividend !== false} />

          <SectionHeader title="Balance Sheet (Latest — Massive)" />
          <MetricRow label="Total Assets" defKey="TOTAL_CASH" value={fmtMoney(bs.total_assets)} />
          <MetricRow label="Total Liabilities" defKey="TOTAL_DEBT" value={fmtMoney(bs.total_liabilities)} />
          <MetricRow label="Total Equity" defKey="BOOK_VALUE" value={fmtMoney(bs.total_equity)} />
          <MetricRow label="Long-Term Debt" defKey="TOTAL_DEBT" value={fmtMoney(bs.long_term_debt)} />
          <MetricRow label="Cash &amp; Equivalents" defKey="TOTAL_CASH" value={fmtMoney(bs.cash_and_equivalents)} />
          <MetricRow
            label="Net Debt"
            defKey="NET_DEBT"
            value={fmtMoney(bs.net_debt)}
            tone={bs.net_debt < 0 ? 'good' : bs.net_debt > 0 ? undefined : undefined}
            hint={bs.net_debt < 0 ? 'Net cash position' : undefined}
          />
          <MetricRow label="Goodwill" defKey="GOODWILL" value={fmtMoney(bs.goodwill)} />
          <MetricRow
            label="Tangible Book Value"
            defKey="TANGIBLE_BOOK"
            value={fmtMoney(bs.tangible_book_value)}
            tone={bs.tangible_book_value < 0 ? 'bad' : undefined}
          />
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground/70 px-1 pt-3">
        Source: Massive. Refreshes daily.{' '}
        {data.income?.period_end && <>Most recent fiscal period ends {data.income.period_end}.</>}
      </p>
    </div>
  );
}

// =========================================================================
// Building blocks — matched to StockMetricsTab's MetricRow + SectionHeader
// styling so every tab in the Stock Modal looks like one coherent surface.
// =========================================================================

function SectionHeader({ title }) {
  return (
    <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400 mt-4 mb-1.5">
      {title}
    </h4>
  );
}

function MetricRow({ label, value, defKey, tone, hint }) {
  const toneClass =
    tone === 'good' ? 'text-emerald-500 dark:text-emerald-400'
    : tone === 'bad' ? 'text-rose-500 dark:text-rose-400'
    : tone === 'neutral' ? 'text-amber-500 dark:text-amber-400'
    : 'text-foreground';
  const labelEl = defKey ? <AbbrTip term={defKey}>{label}</AbbrTip> : label;
  return (
    <div
      className="flex items-baseline justify-between py-1 border-b border-border/40 last:border-b-0"
      data-testid={`fh-row-${(typeof label === 'string' ? label : 'metric').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <span className="text-xs font-bold text-muted-foreground pr-2">{labelEl}</span>
      <span className="text-right">
        <span className={`text-sm font-mono font-semibold ${toneClass}`}>{value}</span>
        {hint && <span className="ml-2 text-[10px] text-muted-foreground/70 align-middle">{hint}</span>}
      </span>
    </div>
  );
}

// 5-year SVG sparklines for Revenue / FCF / Dividends Paid. Plotted
// oldest→newest left-to-right. Helps visualise whether the dividend
// coverage trend is widening (good) or compressing (warning). Each
// data point is hover/tap interactive — hover (desktop) or tap
// (mobile) reveals "FYxxxx: $X.XB" callouts.
function SparklineRow({ history, paysDividend = true }) {
  if (!history || history.length < 2) return null;
  const yearsArr = history.map((r) => r.fiscal_year);
  // Round 96b (Feb 2026) — Hide the Dividends sparkline for non-
  // dividend stocks (3rd cell). Revenue and FCF cells stay because
  // they're independent grid items; collapsing the grid to 2 cols
  // when there's no dividend leg keeps the layout balanced rather
  // than leaving an empty 1/3-width slot.
  return (
    <div className="mt-3 mb-1 rounded-md border border-border/40 bg-muted/20 p-2.5" data-testid="fh-sparklines">
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80 mb-1.5">
        {history.length}-Year Trend ({history[0]?.fiscal_year} – {history[history.length - 1]?.fiscal_year})
      </div>
      <div className={`grid gap-2.5 ${paysDividend ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <SparklineCell
          label="Revenue"
          years={yearsArr}
          values={history.map((r) => r.revenue)}
          tone="emerald"
          testId="fh-spark-revenue"
        />
        <SparklineCell
          label="FCF"
          years={yearsArr}
          values={history.map((r) => r.fcf)}
          tone="sky"
          testId="fh-spark-fcf"
        />
        {paysDividend && (
          <SparklineCell
            label="Dividends"
            years={yearsArr}
            values={history.map((r) => Math.abs(r.dividends_paid || 0))}
            tone="fuchsia"
            testId="fh-spark-dividends"
          />
        )}
      </div>
    </div>
  );
}

function SparklineCell({ label, years, values, tone = 'emerald', testId }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  // Keep only rows where the value exists; preserve year alignment.
  const pairs = (values || []).map((v, i) => ({
    year: years?.[i],
    value: v,
  })).filter((p) => p.value !== null && p.value !== undefined && !Number.isNaN(p.value));
  if (pairs.length < 2) {
    return (
      <div className="text-[10px] text-muted-foreground" data-testid={testId}>
        <div>{label}</div>
        <div className="opacity-60">no history</div>
      </div>
    );
  }
  const cleaned = pairs.map((p) => p.value);
  const first = cleaned[0];
  const last = cleaned[cleaned.length - 1];
  const change = first !== 0 ? (last - first) / Math.abs(first) : null;
  const changeStr =
    change === null ? '—'
    : `${change > 0 ? '+' : ''}${(change * 100).toFixed(0)}%`;
  const changeTone =
    change === null ? 'text-muted-foreground'
    : change > 0.05 ? 'text-emerald-500 dark:text-emerald-400'
    : change < -0.05 ? 'text-rose-500 dark:text-rose-400'
    : 'text-amber-500 dark:text-amber-400';
  const strokeMap = {
    emerald: 'stroke-emerald-500 dark:stroke-emerald-400',
    sky: 'stroke-sky-500 dark:stroke-sky-400',
    fuchsia: 'stroke-fuchsia-500 dark:stroke-fuchsia-400',
  };
  const fillMap = {
    emerald: 'fill-emerald-500/15 dark:fill-emerald-400/15',
    sky: 'fill-sky-500/15 dark:fill-sky-400/15',
    fuchsia: 'fill-fuchsia-500/15 dark:fill-fuchsia-400/15',
  };
  const dotFillMap = {
    emerald: 'fill-emerald-500 dark:fill-emerald-400',
    sky: 'fill-sky-500 dark:fill-sky-400',
    fuchsia: 'fill-fuchsia-500 dark:fill-fuchsia-400',
  };
  // Plot — viewBox in arbitrary units; preserveAspectRatio="none"
  // lets the SVG stretch to container width.
  const W = 100;
  const H = 28;
  const min = Math.min(...cleaned);
  const max = Math.max(...cleaned);
  const range = max - min || 1;
  const step = cleaned.length === 1 ? 0 : W / (cleaned.length - 1);
  const points = cleaned.map((v, i) => {
    const x = i * step;
    const y = H - ((v - min) / range) * (H - 2) - 1;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${path} L${W},${H} L0,${H} Z`;
  // Hit-zone width per point — half the step, clamped so the first
  // and last points still register cleanly.
  const hitW = Math.max(step, 8);

  // Tooltip position (percentage of width — works regardless of
  // container width because SVG is preserveAspectRatio="none")
  const hovered = hoverIdx !== null ? pairs[hoverIdx] : null;
  const hoverX = hoverIdx !== null ? (hoverIdx * step) : 0;
  const tooltipLeftPct = (hoverX / W) * 100;
  // Anchor the tooltip from left/right edges so it doesn't clip
  const anchorLeft = tooltipLeftPct < 50;

  return (
    <div data-testid={testId} className="relative">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`text-[10px] font-mono font-bold ${changeTone}`}>{changeStr}</span>
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-7 mt-0.5"
          preserveAspectRatio="none"
          onPointerLeave={() => setHoverIdx(null)}
        >
          <path d={areaPath} className={fillMap[tone]} strokeWidth="0" />
          <path d={path} className={`${strokeMap[tone]} fill-none`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Visible dot at each point — slightly larger on hover */}
          {points.map(([x, y], i) => (
            <circle
              key={`d-${i}`}
              cx={x}
              cy={y}
              r={hoverIdx === i ? 1.8 : 1.0}
              className={dotFillMap[tone]}
              pointerEvents="none"
            />
          ))}
          {/* Invisible hit zone over each point */}
          {points.map(([x], i) => (
            <rect
              key={`h-${i}`}
              x={Math.max(0, x - hitW / 2)}
              y={0}
              width={hitW}
              height={H}
              fill="transparent"
              onPointerEnter={() => setHoverIdx(i)}
              onPointerDown={() => setHoverIdx(i)}
              data-testid={`${testId}-hit-${pairs[i].year ?? i}`}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </svg>
        {hovered && (
          <div
            className="absolute -top-7 z-20 pointer-events-none whitespace-nowrap rounded-md border border-border/60 bg-popover px-1.5 py-0.5 text-[10px] font-mono font-bold text-popover-foreground shadow-md"
            style={anchorLeft ? { left: `${tooltipLeftPct}%` } : { right: `${100 - tooltipLeftPct}%` }}
            data-testid={`${testId}-tooltip`}
          >
            FY{hovered.year}: {fmtMoney(hovered.value)}
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------- Formatters -----------------------------

function fmtMoney(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const n = Number(value);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(2)}K`;
  return `${sign}$${abs.toFixed(2)}`;
}

function fmtPct(value, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

function fmtRatio(value, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Number(value).toFixed(digits);
}

function fmtMultiple(value, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${Number(value).toFixed(digits)}×`;
}

function coverageTone(v) {
  if (v === null || v === undefined) return undefined;
  if (v >= 1.5) return 'good';
  if (v >= 1.0) return 'neutral';
  return 'bad';
}

function coverageHint(v) {
  if (v === null || v === undefined) return undefined;
  if (v >= 1.5) return 'Comfortable cushion';
  if (v >= 1.0) return 'Adequate';
  return 'Below 1.0 — unsustainable';
}
